// God → Gateway → Ledger: every balance movement across all merchants, newest first.
import { BookOpen } from "lucide-react";
import { god } from "../../godApi";
import { usePagedList, usePolling } from "../../../components/sc/ui";
import { Callout, Cell, Chips, Empty, Page, Pagination, Pill, Section, Table, Toolbar, ago, inr, when } from "../../kit";

const rup = (p) => (p == null ? null : p / 100);
const KINDS = [
  ["all", "All"], ["payin_credit", "Payin credit"], ["payin_adjustment", "Payin adjustment"],
  ["payout_debit", "Payout debit"], ["payout_refund", "Payout refund"], ["manual_adjustment", "Manual adjustment"],
];
const KIND_LABEL = Object.fromEntries(KINDS);
const KIND_TONE = { payin_credit: "green", payin_adjustment: "violet", payout_debit: "blue", payout_refund: "amber", manual_adjustment: "gray" };

export default function GwLedger() {
  const list = usePagedList((p) => god.gwList("ledger", p), { kind: "all" }, 50);
  usePolling(list.load, 10000, list.page === 1);

  return (
    <Page>
      <Section title="Ledger" icon={BookOpen} pad={false} sub="All merchants. Positive amounts credit a balance, negative ones debit it.">
        <Toolbar>
          <Chips options={KINDS.map(([key, label]) => ({ key, label }))} value={list.filters.kind} onChange={(v) => list.setFilter("kind", v)} />
        </Toolbar>
        {list.error && <div className="gwl-pad"><Callout tone="red" title="Couldn't load">{list.error}</Callout></div>}
        <Table testId="god-gw-ledger" rows={list.data.items} loading={list.loading}
          empty={<Empty icon={BookOpen} title={list.filters.kind !== "all" ? "No rows of this type" : "No balance movements yet"}>
            {list.filters.kind !== "all" ? "Try another type." : "Rows appear when payins credit or payouts debit a merchant balance."}
          </Empty>}
          columns={[
            { key: "kind", label: "Type", render: (l) => <Pill tone={KIND_TONE[l.kind] || "gray"}>{KIND_LABEL[l.kind] || l.kind}</Pill> },
            { key: "merchant", label: "Merchant", render: (l) => <Cell main={l.merchant_name || "—"} /> },
            { key: "amt", label: "Amount", align: "right", render: (l) => (
              <b className={l.amount_paisa >= 0 ? "gwl-pos" : "gwl-neg"}>{l.amount_paisa >= 0 ? "+" : "−"}{inr(Math.abs(rup(l.amount_paisa)))}</b>
            ) },
            { key: "bal", label: "Balance after", align: "right", render: (l) => <span className="k-mono">{inr(rup(l.balance_after_paisa))}</span> },
            { key: "ref", label: "Order ref", hideSm: true, render: (l) => (l.order_ref ? <span className="k-mono">{l.order_ref}</span> : <span className="k-faint">—</span>) },
            { key: "note", label: "Note", hideSm: true, render: (l) => (l.note ? <span className="gwl-note">{l.note}</span> : <span className="k-faint">—</span>) },
            { key: "at", label: "Time", render: (l) => <Cell main={when(l.created_at, true)} sub={ago(l.created_at)} /> },
          ]} />
        <Pagination page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
      </Section>

      <style>{`
        .gwl-pad { padding: 12px 18px 0; }
        .gwl-pos { color: #0f7a45; } .gwl-neg { color: #c5221f; }
        .gwl-note { font-size: 13px; color: #374151; overflow-wrap: anywhere; }
      `}</style>
    </Page>
  );
}
